import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { notificationsAPI } from '../services/api';
import HelpAssistant from './HelpAssistant';
import DesktopSyncBar from './DesktopSyncBar';
import toast from 'react-hot-toast';
import {
  LayoutDashboard,
  Calculator,
  ShoppingCart,
  TrendingUp,
  Package,
  Truck,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Warehouse,
  Users,
  Shield,
  Mail,
  Bell,
  Star,
  ChevronLeft,
  ChevronRight,
  Trash2
} from 'lucide-react';

const APP_VERSION = 'v1.0.0';

const Layout = ({ children }) => {
  const displayedVersion = window.ROYSIS_DESKTOP?.version ? `v${window.ROYSIS_DESKTOP.version}` : APP_VERSION;
  const isCloudDesktop = window.ROYSIS_DESKTOP?.mode === 'cloud';
  const syncBarRef = useRef(null);
  const [syncBarHeight, setSyncBarHeight] = useState(0);
  const { user, logout, hasPermission } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const previousUnreadCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationsAPI.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Bildirimler alınamadı:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      const newCount = response.data.count;
      if (newCount > previousUnreadCountRef.current) {
        await fetchNotifications();
      }
      previousUnreadCountRef.current = newCount;
      setUnreadNotifications(newCount);
    } catch (error) {
      console.error('Bildirim sayısı alınamadı:', error);
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isCloudDesktop || !syncBarRef.current) {
      setSyncBarHeight(0);
      return undefined;
    }

    const updateSyncBarHeight = () => {
      setSyncBarHeight(syncBarRef.current?.offsetHeight || 0);
    };

    updateSyncBarHeight();

    const observer = new ResizeObserver(updateSyncBarHeight);
    observer.observe(syncBarRef.current);

    return () => observer.disconnect();
  }, [isCloudDesktop]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    const handleRefresh = () => {
      fetchUnreadCount();
      fetchNotifications();
    };
    window.addEventListener('roysis:notifications-refresh', handleRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener('roysis:notifications-refresh', handleRefresh);
    };
  }, [fetchUnreadCount, fetchNotifications]);

  useEffect(() => {
    if (!showNotifications) {
      return undefined;
    }

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [showNotifications, fetchNotifications]);

  const handleToggleNotifications = () => {
    if (!showNotifications) {
      fetchNotifications();
    }
    setShowNotifications(!showNotifications);
  };

  const handleOpenNotification = async (notification) => {
    setSelectedNotification({
      ...notification,
      is_read: true,
    });
    setShowNotifications(false);

    try {
      await notificationsAPI.markAsRead(notification.id);
      await fetchUnreadCount();
      await fetchNotifications();
    } catch (error) {
      console.error('Bildirim işaretlenemedi:', error);
    }
  };

  const handleNavigateFromNotification = () => {
    if (!selectedNotification?.link) {
      return;
    }

    navigate(selectedNotification.link);
    setSelectedNotification(null);
  };

  const handleToggleNotificationImportant = async () => {
    if (!selectedNotification) {
      return;
    }

    try {
      const response = await notificationsAPI.toggleImportant(selectedNotification.id);
      const isImportant = !!response.data?.isImportant;

      setSelectedNotification((current) => current ? {
        ...current,
        is_important: isImportant ? 1 : 0,
      } : current);

      await fetchNotifications();
      toast.success(isImportant ? 'Bildirim onemli olarak isaretlendi.' : 'Bildirim onemli listesinden kaldirildi.');
    } catch (error) {
      console.error('Bildirim onem durumu guncellenemedi:', error);
    }
  };

  const handleDeleteNotification = async () => {
    if (!selectedNotification) {
      return;
    }

    try {
      await notificationsAPI.deleteNotification(selectedNotification.id);
      setSelectedNotification(null);
      await fetchUnreadCount();
      await fetchNotifications();
      toast.success('Bildirim silindi.');
    } catch (error) {
      console.error('Bildirim silinemedi:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      fetchUnreadCount();
      fetchNotifications();
    } catch (error) {
      console.error('Bildirimler işaretlenemedi:', error);
    }
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Ana Sayfa', permission: 'dashboard' },
    { path: '/accounting', icon: Calculator, label: 'Muhasebe', permission: 'accounting' },
    { path: '/sales', icon: ShoppingCart, label: 'Satış', permission: 'sales' },
    { path: '/orders', icon: ShoppingCart, label: 'Siparişler', permission: 'orders' },
    { path: '/marketing', icon: TrendingUp, label: 'Pazarlama', permission: 'marketing' },
    { path: '/production', icon: Package, label: 'Üretim', permission: 'production' },
    { path: '/shipping', icon: Truck, label: 'Nakliye', permission: 'shipping' },
    { path: '/stock', icon: Warehouse, label: 'Stok', permission: 'stock' },
    { path: '/hr', icon: Users, label: 'İnsan Kaynakları', permission: 'hr' },
    { path: '/messages', icon: Mail, label: 'Mesajlar', permission: 'messages' },
    { path: '/users', icon: Shield, label: 'Kullanıcılar', permission: 'users' },
  ].filter(item => {
    // Tüm menüler için yetki kontrolü
    // Admin her şeye erişebilir (hasPermission içinde kontrol ediliyor)
    return hasPermission(item.permission);
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const selectedNotificationData = selectedNotification
    ? notifications.find((notification) => notification.id === selectedNotification.id) || selectedNotification
    : null;
  const selectedNotificationIndex = selectedNotificationData
    ? notifications.findIndex((notification) => notification.id === selectedNotificationData.id)
    : -1;
  const previousNotification = selectedNotificationIndex > 0
    ? notifications[selectedNotificationIndex - 1]
    : null;
  const nextNotification = selectedNotificationIndex >= 0 && selectedNotificationIndex < notifications.length - 1
    ? notifications[selectedNotificationIndex + 1]
    : null;

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'dark bg-gray-900' : 'bg-slate-100'}`}>
      <div ref={syncBarRef} className="shrink-0 z-50">
        <DesktopSyncBar />
      </div>

      <div className="flex flex-1 min-h-0">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        style={isCloudDesktop ? { top: syncBarHeight } : undefined}
        className={`
        fixed bottom-0 left-0 z-40 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out
        ${isCloudDesktop ? '' : 'top-0'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:top-auto lg:bottom-auto lg:translate-x-0 lg:shrink-0 lg:min-h-full
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                ROYSİS ERP Programı
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Versiyon {displayedVersion}</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5">
            {user?.isDemo && (
              <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded-lg">
                <p className="text-[10px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Demo Modu</p>
                <p className="text-xs text-orange-700 dark:text-orange-300">İşlem limitiniz: 5/modül</p>
                <button 
                  onClick={() => toast('İletişim: +90 (XXX) XXX XX XX', { icon: '📞' })}
                  data-help-target="menu-license"
                  className="mt-2 w-full text-[10px] font-bold bg-orange-600 text-white py-1 rounded hover:bg-orange-700 transition-colors"
                >
                  LİSANS AL
                </button>
              </div>
            )}
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-help-target={`menu-${item.path.replace('/', '') || 'home'}`}
                  className={`
                    flex items-center px-4 py-3 rounded-lg transition-colors duration-200 text-sm
                    ${isActive 
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {user?.role}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={toggleTheme}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <Link
                to="/profile"
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <User className="w-4 h-4" />
              </Link>
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 text-center">
              <p className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] font-bold">
                produced by penguen
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-gray-800/95 backdrop-blur shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <Menu className="w-6 h-6" />
              </button>
              
              <div className="flex items-center space-x-4 ml-auto">
                <div className="relative">
                  <button 
                    onClick={handleToggleNotifications}
                    data-help-target="top-notifications"
                    className="relative p-2 text-gray-500 hover:text-blue-600 transition-colors focus:outline-none"
                  >
                    <Bell className="w-6 h-6" />
                    {unreadNotifications > 0 && (
                      <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full">
                        {unreadNotifications}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setShowNotifications(false)}
                      />
                      <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
                          <h3 className="font-bold text-gray-900 dark:text-white">Bildirimler</h3>
                          {unreadNotifications > 0 && (
                            <button 
                              onClick={handleMarkAllAsRead}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                              Tümünü oku
                            </button>
                          )}
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                          {notifications.length > 0 ? (
                            notifications.map((notification) => (
                              <div 
                                key={notification.id}
                                onClick={() => handleOpenNotification(notification)}
                                className={`p-4 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${!notification.is_read ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}
                              >
                                <div className="flex gap-3">
                                  <div className={`mt-1 p-1.5 rounded-lg ${
                                    notification.type === 'message' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                                    notification.type === 'order' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                                    notification.type === 'production' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' :
                                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                  }`}>
                                    {notification.type === 'message' ? <Mail className="w-4 h-4" /> : 
                                     notification.type === 'order' ? <ShoppingCart className="w-4 h-4" /> : 
                                     notification.type === 'production' ? <Package className="w-4 h-4" /> :
                                     <Bell className="w-4 h-4" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start mb-0.5">
                                      <h4 className={`text-sm ${!notification.is_read ? 'font-bold' : 'font-medium'} text-gray-900 dark:text-white truncate pr-2`}>
                                        {notification.title}
                                      </h4>
                                      {notification.is_important ? (
                                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 flex-shrink-0 mt-0.5" />
                                      ) : null}
                                      {!notification.is_read && (
                                        <span className="w-2 h-2 bg-blue-600 rounded-full mt-1.5 flex-shrink-0"></span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                                      {notification.message}
                                    </p>
                                    <span className="text-[11px] text-blue-600 dark:text-blue-400 mt-2 block font-semibold">
                                      Detayi ac
                                    </span>
                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 block font-medium">
                                      {new Date(notification.created_at).toLocaleString('tr-TR')}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-8 text-center">
                              <Bell className="w-8 h-8 text-gray-300 mx-auto mb-3 opacity-50" />
                              <p className="text-sm text-gray-500 dark:text-gray-400">Henüz bildirim yok</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline-block">
                  {new Date().toLocaleDateString('tr-TR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="main-content">
          {children}
          <p className="px-4 pb-4 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            Produced by Penguen
          </p>
        </main>
      </div>
      </div>
      {selectedNotificationData && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSelectedNotification(null)}
          />
          <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                  Bildirim Detayi
                </p>
                <h3 className="mt-1 text-lg font-bold text-gray-900 dark:text-white">
                  {selectedNotificationData.title}
                </h3>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
                  <Star className={`w-4 h-4 ${selectedNotificationData.is_important ? 'fill-amber-400' : ''}`} />
                  {selectedNotificationData.is_important ? 'Onemli bildirim' : 'Normal bildirim'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => previousNotification && setSelectedNotification(previousNotification)}
                    disabled={!previousNotification}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Onceki
                  </button>
                  <button
                    onClick={() => nextNotification && setSelectedNotification(nextNotification)}
                    disabled={!nextNotification}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Sonraki
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(selectedNotificationData.created_at).toLocaleString('tr-TR')}
              </div>
              <div className="rounded-xl bg-slate-50 dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700 p-4">
                <p className="text-sm leading-6 text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                  {selectedNotificationData.message}
                </p>
              </div>
              {selectedNotificationData.link && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Ilgili sayfa: {selectedNotificationData.link}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleNotificationImportant}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    selectedNotificationData.is_important
                      ? 'text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/40'
                      : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  <Star className={`w-4 h-4 ${selectedNotificationData.is_important ? 'fill-amber-400' : ''}`} />
                  {selectedNotificationData.is_important ? 'Onemden kaldir' : 'Onemli yap'}
                </button>
                <button
                  onClick={handleDeleteNotification}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Sil
                </button>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Kapat
              </button>
              {selectedNotificationData.link && (
                <button
                  onClick={handleNavigateFromNotification}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  Ilgili sayfaya git
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <HelpAssistant currentPath={location.pathname} isDark={isDark} />
    </div>
  );
};

export default Layout;
